import { Controller } from "@hotwired/stimulus"
import * as d3 from "d3"
import AxisDrawer from "./cartesian/drawers/axis_drawer"
import BarChartResponse from "./bar_chart/bar_chart_response"
import BarDrawer from "./bar_chart/bar_drawer"

export default class extends Controller {
    static targets = ["chartName", "xAxisOptionsContainer", "xAxisOptions", "yAxisOptionsContainer", "yAxisOptions",
                      "chart", "chartHeader", "legendContainer"]

    static outlets = ["request"]
    static values = { xAxis: String, yAxis: String }

    async connect() {
        this.connected = true
        this.queueResize = this.queueResize.bind(this)
        const requestNumber = this.refreshRequestNumber = (this.refreshRequestNumber || 0) + 1
        const attributes = await this.requestOutlet.sendGetRequest({url: this.element.dataset.url})
        if (!this.connected) return

        if (requestNumber === this.refreshRequestNumber) {
            this.chartResponse = new BarChartResponse(attributes)
            await this.draw()
        }
        if (!this.connected) return

        this.resizeObserver = new ResizeObserver(this.queueResize)
        this.resizeObserver.observe(this.element)
    }

    disconnect() {
        this.connected = false
        this.refreshRequestNumber++
        this.resizeObserver?.disconnect()
        clearTimeout(this.resizeTimeout)
        cancelAnimationFrame(this.resizeFrame)
    }

    async refresh({ detail: { params } }) {
        const url = new URL(this.element.dataset.url, window.location.origin)
        Object.entries(params).forEach(([key, value]) => url.searchParams.set(key, value ?? ""))
        this.element.dataset.url = url.toString()
        const requestNumber = this.refreshRequestNumber = (this.refreshRequestNumber || 0) + 1
        const attributes = await this.requestOutlet.sendGetRequest({ url: url.toString() })
        if (!this.connected || requestNumber !== this.refreshRequestNumber) return
        this.chartResponse = new BarChartResponse(attributes)
        this.xAxisValue = this.chartResponse.configuration.defaultAxisLabel("x")
        this.yAxisValue = this.chartResponse.configuration.defaultAxisLabel("y")
        await this.draw()
    }

    queueResize() {
        if (!this.connected || !this.chartResponse) return

        clearTimeout(this.resizeTimeout)
        this.resizeTimeout = setTimeout(() => {
            cancelAnimationFrame(this.resizeFrame)
            this.resizeFrame = requestAnimationFrame(() => this.draw())
        }, 150)
    }

    async draw() {
        if (!this.connected || !this.chartResponse || !this.element.clientWidth || !this.element.clientHeight) return

        this.setChartState()
        BarDrawer.drawLegends({
            bars: this.bars,
            legendsContainer: this.legendContainerTarget,
            options: this.chartResponse.options
        })
        this.setSizes()

        const axises = AxisDrawer.drawAxises({
            chartController: this,
            configuration: this.chartResponse.configuration,
            series: this.domainSeries()
        })
        this.xScale = axises.xScale
        this.yScale = axises.yScale
        this.yValueFormatter = axises.yValueFormatter
        this.margin = axises.margin
        this.innerWidth = axises.innerWidth
        this.innerHeight = axises.innerHeight

        const chartG = d3.select(this.chartTarget)
            .append("g")
            .attr("transform", `translate(${this.margin.left}, ${this.margin.top})`)

        BarDrawer.drawBars({
            g: chartG,
            xAxis: { scale: this.xScale },
            yAxis: { scale: this.yScale },
            bars: this.bars,
            options: this.chartResponse.options
        })

        if (this.showHoverLegend()) this.bindHoverLegend()
    }

    setSizes() {
        const styles = getComputedStyle(this.element)
        const headerStyles = getComputedStyle(this.chartHeaderTarget)
        const legendsStyles = getComputedStyle(this.legendContainerTarget)
        const headerSize = this.chartHeaderTarget.getBoundingClientRect()
        const legendSize = this.legendContainerTarget.getBoundingClientRect()

        this.layersAboveSize = 0
        this.chartSize = {
            width: this.chartWidth(),
            height: this.element.clientHeight - parseFloat(styles.paddingTop) - parseFloat(styles.paddingBottom) -
                headerSize.height - parseFloat(headerStyles.marginTop) - parseFloat(headerStyles.marginBottom) -
                legendSize.height - parseFloat(legendsStyles.marginTop) - parseFloat(legendsStyles.marginBottom)
        }

        this.chartTarget.setAttribute("width", this.chartSize.width)
        this.chartTarget.setAttribute("height", this.chartSize.height)
        this.chartTarget.style.overflow = "visible"
    }

    chartWidth() {
        const styles = getComputedStyle(this.element)
        const containerWidth = this.element.clientWidth - parseFloat(styles.paddingLeft) - parseFloat(styles.paddingRight)
        const xAxis = this.chartResponse.configuration.axisByLabel("x", this.xAxisValue)
        const minBandWidth = xAxis?.options?.min_band_width

        if (xAxis?.scaleType !== "band" || !minBandWidth) return containerWidth

        return Math.max(containerWidth, this.xValues().length * minBandWidth)
    }

    setChartState() {
        if (!this.hasXAxisValue) this.xAxisValue = this.chartResponse.configuration.defaultAxisLabel("x")
        if (!this.hasYAxisValue) this.yAxisValue = this.chartResponse.configuration.defaultAxisLabel("y")

        d3.select(this.chartTarget).selectAll("*").remove()
        this.legendContainerTarget.innerHTML = ""

        this.bars = this.chartResponse.barsFor({
            xAxisLabel: this.xAxisValue,
            yAxisLabel: this.yAxisValue
        })
    }

    domainSeries() {
        return this.chartResponse.options.stacked ? this.stackedDomainSeries() : this.groupedDomainSeries()
    }

    groupedDomainSeries() {
        return [
            ...this.bars,
            { data: this.xValues().map(x => [x, 0]) }
        ]
    }

    stackedDomainSeries() {
        const totalsByX = new Map(this.xValues().map(x => [x, 0]))

        this.bars.forEach(bar => {
            bar.data.forEach(([x, y]) => {
                totalsByX.set(x, (totalsByX.get(x) || 0) + y)
            })
        })

        return [{
            data: [
                ...Array.from(totalsByX.entries()),
                ...this.xValues().map(x => [x, 0])
            ]
        }]
    }

    xValues() {
        return Array.from(new Set(this.bars.flatMap(bar => bar.data.map(([x]) => x))))
    }

    showHoverLegend() {
        return this.chartResponse.options.show_hover_value || this.chartResponse.options.show_hover_percentage
    }

    bindHoverLegend() {
        const svg = d3.select(this.chartTarget)
        const domains = this.xScale.domain()

        svg
            .on("mousemove", event => {
                const [mouseX] = d3.pointer(event, event.currentTarget)
                const plotX = mouseX - this.margin.left
                const index = domains.findIndex(domain => {
                    const bandStart = this.xScale(domain)

                    return plotX >= bandStart && plotX <= bandStart + this.xScale.bandwidth()
                })

                this.setLegendValues(index)
            })
            .on("mouseleave", () => this.setLegendValues(-1))
    }

    setLegendValues(index) {
        const xValue = this.xScale.domain()[index]

        this.bars.forEach((bar, barIndex) => {
            const values = this.element.querySelector(`.legend-values[data-index='${barIndex}']`)
            const legendDataElement = values?.querySelector(".legend-data")
            const percentageElement = values?.querySelector(".legend-percentage")
            const dataIndex = bar.data.findIndex(([x]) => x === xValue)
            const value = bar.data[dataIndex]?.[1]

            if (index < 0 || dataIndex < 0) {
                if (legendDataElement) legendDataElement.innerHTML = "--"
                if (percentageElement) percentageElement.innerHTML = "--%"
                percentageElement?.classList?.remove("positive")
                percentageElement?.classList?.remove("negative")
                return
            }

            if (legendDataElement) legendDataElement.innerHTML = this.yValueFormatter(value)
            if (this.chartResponse.options.show_hover_percentage) this.setLegendPercentage(percentageElement, bar.data, dataIndex)
        })
    }

    setLegendPercentage(percentageElement, data, index) {
        if (!percentageElement) return

        if (index === 0) {
            percentageElement.innerHTML = "0%"
            percentageElement.classList.remove("positive")
            percentageElement.classList.remove("negative")
            return
        }

        const newValue = data[index][1]
        const oldValue = data[index - 1]?.[1]
        if (oldValue === undefined) {
            percentageElement.innerHTML = "0%"
            percentageElement.classList.remove("positive")
            percentageElement.classList.remove("negative")
            return
        }

        const percentage = oldValue === 0 ? (newValue > 0 ? -1 : 0) : (oldValue - newValue) / oldValue
        const factor = 10 ** this.chartResponse.options.percentage_precision
        const sign = percentage === 0 ? "" : (percentage < 0 ? "+" : "-")

        percentageElement.innerHTML = `${sign}${Math.abs(Math.round(percentage * factor * 100) / factor)}%`
        percentageElement.classList.toggle("positive", percentage < 0)
        percentageElement.classList.toggle("negative", percentage > 0)
    }
}
